import {inject, Injectable, NgZone, signal} from '@angular/core';
import {DataSnapshot, Database, onValue, push, ref, set, Unsubscribe} from 'firebase/database';
import {FirebaseInitService} from '../shared/firebase-init.service';
import {InvitationNotif, Mention, NotificationBase, Profile, Room} from '@models';
import {PrivateMessageNotification} from '@models';
import {logger} from '../shared';

@Injectable()
export class NotificationService {

	private currentProfile: Profile;
	private readonly invitations = signal<InvitationNotif[]>([]);
	private readonly mentions = signal<Mention[]>([]);
	private readonly privateMessages = signal<PrivateMessageNotification[]>([]);
	private readonly firebaseInit = inject(FirebaseInitService);
	private readonly database: Database = this.firebaseInit.database;
	private readonly ngZone: NgZone = inject(NgZone);
	private baseNotification!: string;
	private notificationListeners: { invitation?: Unsubscribe; mention?: Unsubscribe; privateMessage?: Unsubscribe } = {};
	private readonly notificationsEnabled = true;
	private listeningUid?: string;

	public constructor() {
		this.currentProfile = new Profile();
	}

	/**
	 * Sends a notification to a user.
	 * Uses the recipient's UUID to ensure notifications are sent to the correct user.
	 *
	 * @param type - The type of notification (e.g., 'invitation', 'privateMessage', 'mention')
	 * @param user - The profile of the user who should receive the notification
	 * @param object - The notification object to send
	 */
	public sendNotification(type: string, user: Profile, object: NotificationBase) {
		if (!user.uuid) {
			logger.error('Cannot send notification: user UUID is missing');
			return;
		}
		const recipientNotificationPath = 'notification/' + user.uuid + '/' + type;
		push(ref(this.database, recipientNotificationPath), object.toFirebase());
	}

	private snapshotToNotificationArray<T extends NotificationBase>(snapshot: DataSnapshot, factory: (obj: Record<string, unknown>) => T): T[] {
		const notifications: T[] = [];
		snapshot.forEach((child) => {
			const value = child.val();
			if (typeof value !== 'object' || value === null) {
				return;
			}
			notifications.push(factory({
				...value as Record<string, unknown>,
				uuid: child.key ?? ''
			}));
		});
		return notifications;
	}

	/**
	 * Loads and observes notifications for the current user profile.
	 * Cleans up previous listeners to avoid memory leaks and duplicates.
	 *
	 * @param profile - The profile of the current user
	 */
	loadNotifications(profile: Profile) {
		if (!profile.uuid) {
			logger.error('Cannot load notifications: profile UUID is missing');
			return;
		}

		if (this.listeningUid === profile.uuid && this.notificationListeners.invitation && this.notificationListeners.mention
			&& this.notificationListeners.privateMessage) {
			return;
		}

		// Clean up previous listeners to avoid memory leaks
		this.cleanupListeners();

		this.currentProfile = profile;
		this.baseNotification = 'notification/' + profile.uuid;
		this.listeningUid = profile.uuid;

		// Reset sync arrays to avoid duplicates
		this.invitations.set([]);
		this.mentions.set([]);
		this.privateMessages.set([]);

		if (!this.notificationsEnabled) {
			return;
		}

		// Set up listener for invitation notifications
		this.notificationListeners.invitation = onValue(
			ref(this.database, this.baseNotification + '/invitation'),
			(snapshot) => {
				this.ngZone.run(() => {
					if (!snapshot.exists()) {
						this.invitations.set([]);
						return;
					}
					const invitations = this.snapshotToNotificationArray(snapshot, (obj) => new InvitationNotif(obj));
					this.invitations.set(invitations);
				});
			}
		);

		// Set up listener for mention notifications
		this.notificationListeners.mention = onValue(
			ref(this.database, this.baseNotification + '/mention'),
			(snapshot) => {
				this.ngZone.run(() => {
					if (!snapshot.exists()) {
						this.mentions.set([]);
						return;
					}
					const mentions = this.snapshotToNotificationArray(snapshot, (obj) => new Mention(obj));
					this.mentions.set(mentions);
				});
			}
		);

		// Set up listener for private message notifications
		this.notificationListeners.privateMessage = onValue(
			ref(this.database, this.baseNotification + '/privateMessage'),
			(snapshot) => {
				this.ngZone.run(() => {
					if (!snapshot.exists()) {
						this.privateMessages.set([]);
						return;
					}
					const privateMessages = this.snapshotToNotificationArray(snapshot, (obj) => new PrivateMessageNotification(obj));
					this.privateMessages.set(privateMessages);
				});
			}
		);
	}

	/**
	 * Cleans up Firebase listeners to prevent memory leaks.
	 */
	private cleanupListeners(): void {
		if (this.notificationListeners.invitation) {
			this.notificationListeners.invitation();
			this.notificationListeners.invitation = undefined;
		}
		if (this.notificationListeners.mention) {
			this.notificationListeners.mention();
			this.notificationListeners.mention = undefined;
		}
		if (this.notificationListeners.privateMessage) {
			this.notificationListeners.privateMessage();
			this.notificationListeners.privateMessage = undefined;
		}
		this.listeningUid = undefined;
	}

	public stopNotifications(): void {
		this.cleanupListeners();
		this.invitations.set([]);
		this.mentions.set([]);
		this.privateMessages.set([]);
	}

	listenMention() {
		return this.mentions.asReadonly();
	}

	listenInvitation() {
		return this.invitations.asReadonly();
	}

	listenPrivateMessageNotification() {
		return this.privateMessages.asReadonly();
	}

	/**
	 * Cleans up mention notifications for a specific room.
	 *
	 * @param room - The room for which to clean up mentions
	 */
	cleanMention(room: Room) {
		if (!this.baseNotification) {
			return;
		}
		const mentionsToClean = this.mentions().filter(mention => mention.rid === room.uuid);
		mentionsToClean.forEach(mention => {
			if (mention.uuid) {
				set(ref(this.database, this.baseNotification + '/mention/' + mention.uuid), null);
			}
		});
		// Update local sync array
		this.mentions.set(this.mentions().filter(mention => mention.rid !== room.uuid));
	}

	/**
	 * Deletes invitation notifications from a specific sender.
	 *
	 * @param sender - The UID of the sender whose invitations should be deleted
	 */
	deleteInvitationNotification(sender: string) {
		if (!this.baseNotification) {
			return;
		}
		const invitationsToDelete = this.invitations().filter(invit => invit.inviteID === sender);
		invitationsToDelete.forEach(invitation => {
			if (invitation.uuid) {
				set(ref(this.database, this.baseNotification + '/invitation/' + invitation.uuid), null);
			}
		});
		// Update local sync array
		this.invitations.set(this.invitations().filter(invit => invit.inviteID !== sender));
	}

	/**
	 * Cleans up private message notifications for a specific room.
	 * Called when the user opens a room to read messages.
	 *
	 * @param room - The room for which to clean up private message notifications
	 */
	cleanPrivateMessageNotifications(room: Room) {
		if (!this.baseNotification) {
			return;
		}
		const notificationsToClean = this.privateMessages().filter(notification => notification.rid === room.uuid);
		notificationsToClean.forEach(notification => {
			if (notification.uuid) {
				set(ref(this.database, this.baseNotification + '/privateMessage/' + notification.uuid), null);
			}
		});
		// Update local sync array
		this.privateMessages.set(this.privateMessages().filter(notification => notification.rid !== room.uuid));
	}
}
